/**
 * ハロー効果チャートコンポーネント
 *
 * 評価カテゴリ間の相関を可視化してハロー効果を検出
 * 科学的根拠: Thorndike (1920), Nisbett & Wilson (1977)
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { bg } from '../../../../utils/darkModeAdapter';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';

/**
 * ハロー効果チャート
 * @param {Object} data - ハロー効果分析データ
 */
export default function HaloEffectChart({ data }) {
  const theme = useTheme();

  if (!data) {
    return (
      <Alert severity="info">
        ハロー効果分析データがありません
      </Alert>
    );
  }

  const { correlation_matrix, detected_halo_effects, overall_halo_score } = data;

  // 相関係数に応じた色を取得
  const getCorrelationColor = (value) => {
    const absValue = Math.abs(value);
    if (absValue >= 0.8) return '#ef4444'; // 非常に高い相関（ハロー効果の疑い）
    if (absValue >= 0.6) return '#f59e0b'; // 高い相関
    if (absValue >= 0.4) return '#fbbf24'; // 中程度の相関
    return '#22c55e'; // 低い相関（健全）
  };

  // 相関の強さラベル
  const getCorrelationLabel = (value) => {
    const absValue = Math.abs(value);
    if (absValue >= 0.8) return '非常に強い';
    if (absValue >= 0.6) return '強い';
    if (absValue >= 0.4) return '中程度';
    return '弱い';
  };

  return (
    <Box>
      {/* 概要 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="subtitle2">
          ハロー効果スコア:
        </Typography>
        <Chip
          label={`${overall_halo_score || 0}点`}
          color={
            (overall_halo_score || 0) > 50 ? 'error' :
            (overall_halo_score || 0) > 20 ? 'warning' : 'success'
          }
          icon={
            (overall_halo_score || 0) > 50 ? <WarningIcon /> :
            (overall_halo_score || 0) > 20 ? <InfoIcon /> : <CheckCircleIcon />
          }
        />
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>ハロー効果とは:</strong> ある特性の評価が他の特性の評価に影響を与える認知バイアスです。
          評価カテゴリ間の相関が高すぎる場合（r≥0.8）、ハロー効果が疑われます。
          <br />
          <em>参考: Thorndike (1920), Nisbett & Wilson (1977)</em>
        </Typography>
      </Alert>

      {/* 検出されたハロー効果 */}
      {detected_halo_effects && detected_halo_effects.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            検出されたハロー効果:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {detected_halo_effects.map((effect, index) => (
              <li key={index}>
                <Typography variant="body2">
                  「{effect.category1}」と「{effect.category2}」の相関が非常に高い（r={effect.correlation.toFixed(2)}）
                </Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* 相関マトリクス */}
      {correlation_matrix && Object.keys(correlation_matrix).length > 0 && (
        <Paper sx={{ mb: 2, overflow: 'hidden' }}>
          <Typography variant="subtitle2" sx={{ p: 2, backgroundColor: bg(theme).subtle }}>
            カテゴリ間相関マトリクス
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  {Object.keys(correlation_matrix).map(category => (
                    <TableCell key={category} align="center" sx={{ fontWeight: 600 }}>
                      {category}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(correlation_matrix).map(([rowCategory, correlations]) => (
                  <TableRow key={rowCategory}>
                    <TableCell sx={{ fontWeight: 600 }}>{rowCategory}</TableCell>
                    {Object.entries(correlations).map(([colCategory, value]) => (
                      <TableCell
                        key={colCategory}
                        align="center"
                        sx={{
                          backgroundColor: rowCategory === colCategory ? '#f1f5f9' :
                            `${getCorrelationColor(value)}20`,
                          color: rowCategory === colCategory ? '#94a3b8' :
                            getCorrelationColor(value)
                        }}
                      >
                        <Tooltip
                          title={
                            rowCategory === colCategory ? '同一カテゴリ' :
                            `${getCorrelationLabel(value)}相関`
                          }
                        >
                          <span style={{ fontWeight: Math.abs(value) >= 0.6 ? 'bold' : 'normal' }}>
                            {value.toFixed(2)}
                          </span>
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* 凡例 */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#22c55e', borderRadius: 1 }} />
          <Typography variant="caption">弱い相関 (&lt;0.4)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#fbbf24', borderRadius: 1 }} />
          <Typography variant="caption">中程度 (0.4-0.6)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#f59e0b', borderRadius: 1 }} />
          <Typography variant="caption">強い相関 (0.6-0.8)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, backgroundColor: '#ef4444', borderRadius: 1 }} />
          <Typography variant="caption">ハロー効果疑い (≥0.8)</Typography>
        </Box>
      </Box>
    </Box>
  );
}
